import type { OperationItem } from '@ops/entities/operation-item';
import type { SelectOption } from '@/shared/ui';
import { ChevronLeft, ChevronRight, Handshake, Users } from 'lucide-react';
import {
  Button,
  Checkbox,
  EmptyState,
  Pagination,
  PaginationButton,
  Select,
  TableCell,
  TableContainer,
  TableFooter,
  TableRow,
  TableSkeletonRow,
  Text,
} from '@/shared/ui';
import {
  DASHBOARD_COLUMNS,
  DASHBOARD_ROW_MIN,
  SERVICE_BASIS,
} from '../lib/columns';
import { toDashboardRow } from '../lib/row-view';
import { DashboardRow } from './dashboard-row';
import css from './items-table.module.scss';

type ItemsTableProps = {
  items: OperationItem[];
  isLoading: boolean;
  isError: boolean;
  total: number;
  /**
   * Item-row total used for pagination (XT-380). With a countMode card the
   * API's `total` counts itineraries/messages while offset/limit slice item
   * rows, so pages derive from this instead. Falls back to `total` when unset.
   */
  rowTotal?: number;
  offset: number;
  limit: number;
  /** id → org status colour, for the per-status status dot. */
  statusColorById: Map<string, string | null>;
  /** Org statuses for the bulk "Set status" picker. */
  statusOptions: SelectOption[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleAll: (ids: string[], selected: boolean) => void;
  onClearSelection: () => void;
  onBulkAssignDriverGuide: () => void;
  onBulkSetStatus: (statusId: string) => void;
  onBulkAssignSupplier: () => void;
  onOpenItinerary: (itineraryId: string) => void;
  onOpenInOperations: (itineraryId: string) => void;
  onToggleFlag: (item: OperationItem) => void;
  onPageChange: (offset: number) => void;
};

// Header labels paired with their column sizing (kept in the same order as the
// cells in DashboardRow so the flex columns line up).
// XT-380 drill-in columns: item name (Service), itinerary ref, location,
// owner, op_status — plus When / Driver·guide from the v2 design.
const HEADERS: { label: string; width?: string }[] = [
  { label: 'When', width: DASHBOARD_COLUMNS.when },
  { label: 'Service' },
  { label: 'Itinerary · traveller', width: DASHBOARD_COLUMNS.itinerary },
  { label: 'Location', width: DASHBOARD_COLUMNS.location },
  { label: 'Owner', width: DASHBOARD_COLUMNS.owner },
  { label: 'Driver / guide', width: DASHBOARD_COLUMNS.operator },
  { label: 'Status', width: DASHBOARD_COLUMNS.status },
];

// Skeleton columns mirror the real row layout (fixed columns pinned by basis
// with flexGrow 0, the Service column growable) so the loading placeholder
// lines up with the header + real rows. `width` is the shimmer bar's width.
const SKELETON_COLUMNS = [
  { basis: DASHBOARD_COLUMNS.select, flexGrow: 0, width: '16px' },
  { basis: DASHBOARD_COLUMNS.when, flexGrow: 0, width: '55%' },
  { basis: SERVICE_BASIS, flexGrow: 1, width: '75%' },
  { basis: DASHBOARD_COLUMNS.itinerary, flexGrow: 0, width: '80%' },
  { basis: DASHBOARD_COLUMNS.location, flexGrow: 0, width: '60%' },
  { basis: DASHBOARD_COLUMNS.owner, flexGrow: 0, width: '60%' },
  { basis: DASHBOARD_COLUMNS.operator, flexGrow: 0, width: '70%' },
  { basis: DASHBOARD_COLUMNS.status, flexGrow: 0, width: '55%' },
  { basis: DASHBOARD_COLUMNS.actions, flexGrow: 0, width: '40%' },
];
const SKELETON_ROW_COUNT = 8;

export const ItemsTable = ({
  items,
  isLoading,
  isError,
  total,
  rowTotal,
  offset,
  limit,
  statusColorById,
  statusOptions,
  selectedIds,
  onToggleSelect,
  onToggleAll,
  onClearSelection,
  onBulkAssignDriverGuide,
  onBulkSetStatus,
  onBulkAssignSupplier,
  onOpenItinerary,
  onOpenInOperations,
  onToggleFlag,
  onPageChange,
}: ItemsTableProps) => {
  const pageIds = items.map((item) => item.id ?? '').filter(Boolean);
  const selectedCount = selectedIds.size;
  const allSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const currentPage = Math.floor(offset / limit) + 1;
  // Pagination runs over item ROWS, which may differ from the displayed
  // card-mode `total` (itineraries/messages).
  const pageTotal = rowTotal ?? total;
  const totalPages = Math.max(1, Math.ceil(pageTotal / limit));

  return (
    <div className={css.root}>
      {/* Bulk bar sits OUTSIDE the scrollable table so it stays pinned to the
          viewport width instead of scrolling off-screen with the table body. */}
      {selectedCount > 0 && (
        <div className={css.bulkBar}>
          <span className={css.bulkSelected}>{selectedCount} selected</span>
          <div className={css.bulkActions}>
            <Button
              icon={<Users style={{ height: 14, width: 14 }} />}
              onClick={onBulkAssignDriverGuide}
              size="sm"
              type="button"
              variant="outline"
            >
              Assign driver / guide
            </Button>
            <Select
              onChange={(e) =>
                e.target.value && onBulkSetStatus(e.target.value)
              }
              options={statusOptions}
              placeholder="Set status"
              size="compact"
              usePortal
              value=""
            />
            <Button
              icon={<Handshake style={{ height: 14, width: 14 }} />}
              onClick={onBulkAssignSupplier}
              size="sm"
              type="button"
              variant="outline"
            >
              Assign supplier
            </Button>
            <Button
              onClick={onClearSelection}
              size="sm"
              type="button"
              variant="ghost"
            >
              Clear
            </Button>
          </div>
        </div>
      )}

      <TableContainer scrollable>
        <TableRow header minWidth={DASHBOARD_ROW_MIN}>
          <TableCell width={DASHBOARD_COLUMNS.select}>
            <Checkbox
              aria-label="Select all"
              checked={allSelected}
              onChange={() => onToggleAll(pageIds, !allSelected)}
            />
          </TableCell>
          {HEADERS.map(({ label, width }) =>
            width ? (
              <TableCell className={css.headerCell} key={label} width={width}>
                {label}
              </TableCell>
            ) : (
              <TableCell
                basis={SERVICE_BASIS}
                className={css.headerCell}
                flexGrow={1}
                key={label}
              >
                {label}
              </TableCell>
            )
          )}
          <TableCell width={DASHBOARD_COLUMNS.actions}> </TableCell>
        </TableRow>

        {isError ? (
          <div className={css.empty}>
            <EmptyState
              description="Could not load operations. Try again."
              title="Something went wrong"
            />
          </div>
        ) : isLoading ? (
          Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => (
            <TableSkeletonRow
              columns={SKELETON_COLUMNS}
              key={i}
              minWidth={DASHBOARD_ROW_MIN}
            />
          ))
        ) : items.length === 0 ? (
          <div className={css.empty}>
            <EmptyState
              description="No items match this view for the selected window."
              title="Nothing here"
            />
          </div>
        ) : (
          items.map((item) => {
            const row = toDashboardRow(item);
            return (
              <DashboardRow
                item={item}
                key={row.id || item.id || row.itineraryRef}
                onOpenInOperations={onOpenInOperations}
                onOpenItinerary={onOpenItinerary}
                onToggleFlag={onToggleFlag}
                onToggleSelect={onToggleSelect}
                row={row}
                selected={selectedIds.has(row.id)}
                statusColor={
                  row.statusId
                    ? (statusColorById.get(row.statusId) ?? null)
                    : null
                }
              />
            );
          })
        )}

        {pageTotal > limit && (
          <TableFooter className={css.footer}>
            <Pagination>
              <PaginationButton
                aria-label="Previous page"
                disabled={currentPage <= 1}
                onClick={() => onPageChange((currentPage - 2) * limit)}
              >
                <ChevronLeft style={{ height: 14, width: 14 }} />
              </PaginationButton>
              <Text size="xs" variant="muted">
                {currentPage} / {totalPages}
              </Text>
              <PaginationButton
                aria-label="Next page"
                disabled={currentPage >= totalPages}
                onClick={() => onPageChange(currentPage * limit)}
              >
                <ChevronRight style={{ height: 14, width: 14 }} />
              </PaginationButton>
            </Pagination>
          </TableFooter>
        )}
      </TableContainer>
    </div>
  );
};
